import type { ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  getSaTravelPersonItems,
  getSaTravelRequestCost,
  SA_TRAVEL_REQUEST_COST_KEYS,
  saTravelText,
  type SaTravelHeader,
} from '@/utils/approval/saTravelExpense';
import {
  LegacyLinkIcon,
  NewWindowLink,
  PortalLinkIcon,
  useSaTravelSnapshotNodes,
} from './index';
import { SaTravelRequestRoot } from './styles';

type TableProps = {
  header: SaTravelHeader;
  t: TFunction;
  linkIcon: ReactNode;
};

const DAILY_ALLOWANCE_ROWS = [
  {
    label: '임원',
    cells: [
      '-',
      'CNY 600',
      'CNY 500',
      '-',
      'USD 90',
      'USD 80',
      'USD 70',
      'USD 60',
    ],
  },
  {
    label: '직원',
    cells: [
      '-',
      'CNY 500',
      'CNY 450',
      '-',
      'USD 80',
      'USD 70',
      'USD 60',
      'USD 50',
    ],
  },
];

const GRADES = ['1급지', '2급지', '3급지', '4급지'];

/** 레거시 #eas_travel_box 안쪽 마크업(class·id·SAP 키 동일) */
const SaTravelRequestBody = ({ header, t, linkIcon }: TableProps) => {
  const text = (key: keyof SaTravelHeader) => saTravelText(header[key]);
  const items = getSaTravelPersonItems(header) ?? [];
  const cost = getSaTravelRequestCost(header);

  return (
    <>
      <div className="text-center p-30">
        <p className="mb-20">
          {t(
            '해외출장 사전 품의를 아래와 같이 진행하고자 하오니 검토 후 재가 바랍니다.',
          )}
        </p>
        <p className="pt-10">{t('- 아 래 -')}</p>
      </div>

      <div className="mt-20">
        <strong className="mb-5 d-block">{t('1. 기본정보')}</strong>
        <table className="table table-orders table-bordered table-fixed mb-10">
          <colgroup>
            <col width="20%" />
            <col width="*" />
          </colgroup>
          <tbody>
            <tr>
              <th className="text-center">{t('일비수령 여부')}</th>
              <td id="Zuse">
                {header.Zuse === 'Y' ? t('일비수령 신청 건입니다.') : ''}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="w-100 mb-10">
          <table className="table table-orders table-bordered table-fixed no-margin">
            <colgroup>
              <col width="20%" />
              <col width="*" />
              <col width="20%" />
              <col width="*" />
              <col width="64px" />
            </colgroup>
            <tbody>
              <tr>
                <th className="text-center">{t('출장코드')}</th>
                <td id="Trvnr">{text('Trvnr')}</td>
                <th className="text-center">{t('출장국가')}</th>
                <td id="Land1Fr">{text('Land1Fr')}</td>
                <th className="text-center">{t('상세')}</th>
              </tr>
              <tr>
                <th className="text-center">{t('예산부서')}</th>
                <td id="KostlT">{text('KostlT')}</td>
                <th className="text-center">{t('출장자명')}</th>
                <td id="PersonNm">{text('PersonNm')}</td>
                <td id="UrlLink1" className="text-center" rowSpan={3}>
                  <NewWindowLink href={text('Urllink1')} icon={linkIcon} />
                </td>
              </tr>
              <tr>
                <th className="text-center">{t('출장기간')}</th>
                <td id="Trvdat">{text('Trvdat')}</td>
                <th className="text-center">{t('출장인원수')}</th>
                <td id="Zcount">{text('Zcount')}</td>
              </tr>
              <tr>
                <th className="text-center">{t('출장목적')}</th>
                <td id="Trvtxt" colSpan={3}>
                  {text('Trvtxt')}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-40">
        <strong className="mb-5 d-block">{t('2. 출장정보')}</strong>
        <div className="pb-10">
          <span>{t('1) 출장일정')}</span>
          <table className="table table-orders table-bordered table-fixed text-center">
            <colgroup>
              <col width="56px" />
              <col width="*" />
              <col width="*" />
              <col width="46px" />
              <col width="*" />
              <col width="*" />
              <col width="*" />
            </colgroup>
            <thead>
              <tr>
                <th>{t('사원명')}</th>
                <th>{t('시작일자')}</th>
                <th>{t('종료일자')}</th>
                <th>{t('출장일수')}</th>
                <th>{t('출발지')}</th>
                <th>{t('도착지')}</th>
                <th>{t('급지(일비)')}</th>
              </tr>
            </thead>
            <tbody id="infoTbody">
              {items.map((item, index) => (
                <tr key={index}>
                  <td>{item.Pernr || ''}</td>
                  <td>{item.DatumFr || ''}</td>
                  <td>{item.DatumTo || ''}</td>
                  <td>{item.Trdays || ''}</td>
                  <td>{item.CityFr || ''}</td>
                  <td>{item.CityTo || ''}</td>
                  <td>{item.StateTo || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pb-10">
          <span>{t('2) 항공 및 숙박내역')}</span>
          <table className="table table-orders table-bordered table-fixed text-center">
            <colgroup>
              <col width="140px" />
              <col width="70px" />
              <col width="*" />
              <col width="*" />
            </colgroup>
            <thead>
              <tr>
                <th>{t('항공사편명')}</th>
                <th>{t('좌석')}</th>
                <th>{t('호텔명')}</th>
                <th>{t('특이사항')}</th>
              </tr>
            </thead>
            <tbody id="expenseTbody">
              {items.map((item, index) => (
                <tr key={index}>
                  <td>{item.Airplain || ''}</td>
                  <td>{item.Seat || ''}</td>
                  <td>{item.Hotel || ''}</td>
                  {index === 0 && (
                    <td rowSpan={items.length} className="text-left">
                      {header.Remrk || ''}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <span>{t('3) 출장비용')}</span>
          <table className="table table-orders table-bordered table-fixed text-center mb-10">
            <colgroup>
              <col width="56px" />
              <col width="*" />
              <col width="*" />
              <col width="*" />
              <col width="*" />
              <col width="*" />
              <col width="*" />
              <col width="90px" />
              <col width="90px" />
              <col width="70px" />
              <col width="72px" />
            </colgroup>
            <thead>
              <tr>
                <th>{t('사원명')}</th>
                <th>{t('항공권')}</th>
                <th>{t('숙박비')}</th>
                <th>{t('비자')}</th>
                <th>{t('보험')}</th>
                <th>{t('기타')}</th>
                <th>{t('기타내용')}</th>
                <th>{t('일비')}</th>
                <th>{t('인별 합계')}</th>
                <th>{t('사용수단')}</th>
                <th>{t('사유')}</th>
              </tr>
            </thead>
            <tbody id="costTbody">
              {cost?.rows.map(({ item, total }, index) => (
                <tr key={index}>
                  <td>{item.Pernr || ''}</td>
                  <td>{item.Exp01 || ''}</td>
                  <td>{item.Exp02 || ''}</td>
                  <td>{item.Exp03 || ''}</td>
                  <td>{item.Exp04 || ''}</td>
                  <td>{item.Exp05 || ''}</td>
                  <td>{item.Etc || ''}</td>
                  <td>{item.Zpay || ''}</td>
                  <td>{total}</td>
                  <td>{item.Zuse || ''}</td>
                  <td>{item.Deduc || ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot id="costTfoot">
              {cost && (
                <tr className="total-row">
                  <td>{t('합계')}</td>
                  {SA_TRAVEL_REQUEST_COST_KEYS.slice(0, 5).map((key) => (
                    <td key={key}>{cost.sums[key]}</td>
                  ))}
                  <td />
                  <td>{cost.sums.Zpay}</td>
                  <td>{cost.total}</td>
                  <td colSpan={2} />
                </tr>
              )}
            </tfoot>
          </table>
          <ul>
            <li>
              {t(
                '* 해외로밍비, 해외샘플대는 해외출장비 예산에 포함되지 않으며, 별도 정산 진행하시기 바랍니다.',
              )}
            </li>
            <li>
              {t(
                '* 각 항목별 초과사용률이 30%를 초과한 경우 사유서를 작성해주셔야 비용 정산이 가능합니다.',
              )}
            </li>
            <li>
              {t(
                '* 기타 비용의 경우 30% 초과와 상관없이 사전품의 예산을 초과하는 경우 기타품의 작성 후 정산이 가능합니다.',
              )}
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-40">
        <strong className="mb-5 d-block">{t('3. 출장실적')}</strong>
        <div className="pb-10">
          <span>{t('1) 해외출장비 예산내역')}</span>
          <table className="table table-orders table-bordered table-fixed text-center mb-10">
            <thead>
              <tr>
                <th>{t('코스트센터')}</th>
                <th>{t('코스트센터명')}</th>
                <th>{t('예산')}</th>
                <th>{t('실적')}</th>
                <th>{t('가용예산')}</th>
              </tr>
            </thead>
            <tbody id="performanceTbody">
              <tr>
                <td>{text('Kostl')}</td>
                <td>{text('KostlT')}</td>
                <td>{text('BudgetTotl')}</td>
                <td>{text('ActualTotl')}</td>
                <td>{text('Remain')}</td>
              </tr>
            </tbody>
          </table>
          <ul>
            <li>
              {t(
                '* 시즌예산은 현 시점 기준으로 실제 정산시와 상이할 수 있습니다.',
              )}
            </li>
            <li>
              {t(
                '* 출장 후 정산 시점에 예산이 부족할 경우에 비용정산이 불가능하며, 필요시 예산 증액 후 정산이 가능합니다.',
              )}
            </li>
            <li>
              {t(
                '* 실적 : 시즌내 여비교통비_해외출장비 실적 +일비(일비외 항공권, 숙박비, 비자, 보험 등은 정산전표 생성시점에 실적반영)',
              )}
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-40">
        <strong className="mb-10 d-block">{t('[ 일비참고사항 ]')}</strong>
        <div className="mb-40">
          <span>{t('※ 출장일비')}</span>
          <table className="table table-orders table-bordered table-fixed text-center mt-5 mb-10">
            <thead>
              <tr>
                <th rowSpan={2}>{t('구분')}</th>
                <th colSpan={4}>{t('중국')}</th>
                <th colSpan={4}>{t('중국 외')}</th>
              </tr>
              <tr>
                {[...GRADES, ...GRADES].map((grade, index) => (
                  <th key={index}>{t(grade)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAILY_ALLOWANCE_ROWS.map((row) => (
                <tr key={row.label}>
                  <th>{t(row.label)}</th>
                  {row.cells.map((cell, index) => (
                    <td key={index}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="d-flex">
            <div>{t('⌜주⌟')}</div>
            <div className="ml-5">
              <div>
                <span>{t('① 해외출장 일비는 개인형 법인카드 사용 원칙')}</span>
                <ul className="ml-20">
                  <li>
                    {t('- 중국은 UnionPay 필요 시, 공용 법인카드 불출 가능')}
                  </li>
                  <li>
                    {t(
                      '- 단, 불가피한 사유로 일비 수령이 필요한 국가에 한하여 일비 지급 가능',
                    )}
                  </li>
                </ul>
              </div>
              <div className="pt-10">
                {t(
                  "② '일비' 카테고리의 경우 기준 금액을 초과하여 사용 불가합니다.",
                )}
              </div>
              <div className="pt-10">
                {t(
                  "③ '일비'는 해외출장시 1일 기준으로 지급되는 식비 및 대중교통비로 사용하는 항목입니다.",
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="pb-20">
          <span>{t('※ 출장급지')}</span>
          <ul className="mt-5">
            <li>
              {t(
                '1) 1급지 : 일본(도쿄), 미국(뉴욕, LA, 샌프란시스코, 워싱턴), 홍콩, 파리, 런던, 스위스 등',
              )}
            </li>
            <li>
              {t(
                '2) 2급지 : 중국(베이징, 상해), 일본(기타), 미국(기타), 이탈리아, 독일, 러시아, 스페인 등',
              )}
            </li>
            <li>
              {t(
                '3) 3급지 : 중국(기타), 베트남, 타이, 인도네시아, 뉴질랜드, 터키 등',
              )}
            </li>
            <li>{t('4) 4급지 : 미얀마, 캄보디아, 몽골, 필리핀 등')}</li>
          </ul>
        </div>
      </div>
    </>
  );
};

/** 작성 화면 — 저장 content 와 같은 마크업 */
export const SaTravelRequestView = ({
  header,
  t,
}: Omit<TableProps, 'linkIcon'>) => (
  <SaTravelRequestRoot>
    <SaTravelRequestBody header={header} t={t} linkIcon={<PortalLinkIcon />} />
  </SaTravelRequestRoot>
);

/** attributes.content — 레거시 저장 범위(#eas_travel_box 안쪽)와 같음 */
export const buildSaTravelRequestContentHtml = (
  header: SaTravelHeader,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <SaTravelRequestBody header={header} t={t} linkIcon={<LegacyLinkIcon />} />,
  );

/** 저장 content(레거시 문서 포함) 그대로 */
export const SaTravelRequestSnapshot = ({ content }: { content: string }) => (
  <SaTravelRequestRoot>{useSaTravelSnapshotNodes(content)}</SaTravelRequestRoot>
);
